// Helpers de exportação específicos da tela de Relatórios (/reports) —
// deliberadamente independentes dos helpers internos de
// route-history-center.tsx (feature separada, não mexemos lá). Cobre os
// dois formatos que o Traccar não gera nativamente para os relatórios
// derivados (Ligado/Desligado, Dirigindo/Parado, Comandos, Informações
// Gerais): Excel via a lib "xlsx" já usada no projeto, e PDF via impressão
// do navegador numa janela isolada (sem lib de PDF nova).
import { loadXlsx } from "@/lib/xlsx-lazy";

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR");
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${m > 0 ? ` ${m}min` : ""}`;
}

export interface ExportSheet {
  name: string;
  rows: (string | number)[][];
}

// Workbook multi-aba genérico — cada relatório monta suas próprias linhas
// (já com dados reais buscados no Traccar) e passa aqui só para virar
// arquivo. Nome de aba limitado a 31 caracteres (limite do formato xlsx).
export async function exportWorkbook(sheets: ExportSheet[], filename: string) {
  const XLSX = await loadXlsx();
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet.rows), sheet.name.slice(0, 31));
  }
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadBlob(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    filename,
  );
}

// PDF = impressão numa janela isolada com HTML próprio, sem tocar no layout
// do app (sem popup de lib nova, sem esconder/mostrar a tela real). O
// chamador monta o corpo em HTML simples (títulos + tabelas) com os dados
// já formatados.
// Imprime num <iframe> escondido em vez de window.open(): os relatórios só
// chamam isso depois que o dado chega (fora do clique do usuário), e aí o
// bloqueador de pop-up do navegador barrava a janela nova em silêncio —
// "baixo o PDF e não gera nada". print() de iframe não exige gesto do
// usuário. Espera as imagens (tiles do mapa) carregarem antes de imprimir,
// senão o mapa sai em branco no PDF.
export function printReport(title: string, bodyHtml: string) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = iframe.contentDocument ?? win?.document;
  if (!win || !doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(`<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #1a1a1a; margin: 32px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .subtitle { color: #666; font-size: 12px; margin-bottom: 20px; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: #444; margin: 24px 0 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
  th, td { text-align: left; padding: 5px 8px; border-bottom: 1px solid #eee; }
  th { color: #555; font-weight: 600; background: #fafafa; }
  .meta { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 4px; font-size: 12px; color: #444; }
  .meta b { color: #111; }
  .route-map { page-break-inside: avoid; break-inside: avoid; margin-bottom: 16px; }
  @media print { body { margin: 12mm; } }
</style>
</head>
<body>${bodyHtml}</body>
</html>`);
  doc.close();

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } finally {
      // print() bloqueia até o diálogo fechar na maioria dos navegadores;
      // o atraso cobre os que retornam na hora (Safari).
      setTimeout(() => iframe.remove(), 60_000);
    }
  };
  const imgs = Array.from(doc.images);
  const pending = imgs.filter((img) => !img.complete);
  if (pending.length === 0) {
    setTimeout(doPrint, 100);
    return;
  }
  let left = pending.length;
  const done = () => {
    left -= 1;
    if (left <= 0) setTimeout(doPrint, 100);
  };
  for (const img of pending) {
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  }
  // Rede de segurança: tile lento/travado não pode segurar o PDF pra sempre.
  setTimeout(doPrint, 10_000);
}

// ---------------------------------------------------------------------------
// Mapa do trajeto pro PDF — imagens de tile do OpenStreetMap montadas lado a
// lado + traçado em SVG por cima. É HTML/<img> puro, então imprime normal
// (o mapa interativo do Google não sai em impressão, e a Static Maps API do
// Google não está habilitada na chave — devolve 403, ver memória
// gpsdrive_contract_map_blank_fix). Sem chave, sem lib nova.
// ---------------------------------------------------------------------------

export interface PrintRouteLine {
  color: string;
  points: { lat: number; lng: number }[];
}

const TILE = 256;

function mercX(lng: number, z: number) {
  return ((lng + 180) / 360) * TILE * 2 ** z;
}
function mercY(lat: number, z: number) {
  const r = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * TILE * 2 ** z;
}

// Reduz o número de pontos mantendo início/fim — milhares de vértices no SVG
// não acrescentam nada visualmente e pesam no PDF.
export function downsamplePoints<T>(points: T[], max: number): T[] {
  if (points.length <= max) return points;
  const step = (points.length - 1) / (max - 1);
  const out: T[] = [];
  for (let i = 0; i < max; i++) out.push(points[Math.round(i * step)]);
  return out;
}

export interface TileViewport {
  tiles: { url: string; x: number; y: number }[];
  // Pixel (x,y) dentro da caixa width×height pro lat/lng informado.
  project: (lat: number, lng: number) => [number, number];
}

// Escolhe o maior zoom em que todos os pontos cabem na caixa (com margem) e
// lista os tiles OSM que cobrem a área. Usado pelo PDF dos Relatórios e
// pelo PDF do Centro de Histórico.
export function computeTileViewport(
  points: { lat: number; lng: number }[],
  width: number,
  height: number,
  pad = 30,
): TileViewport | null {
  if (points.length === 0) return null;
  const minLat = Math.min(...points.map((p) => p.lat));
  const maxLat = Math.max(...points.map((p) => p.lat));
  const minLng = Math.min(...points.map((p) => p.lng));
  const maxLng = Math.max(...points.map((p) => p.lng));
  let z = 17;
  for (; z > 1; z--) {
    const w = mercX(maxLng, z) - mercX(minLng, z);
    const h = mercY(minLat, z) - mercY(maxLat, z);
    if (w <= width - pad * 2 && h <= height - pad * 2) break;
  }
  const cx = (mercX(minLng, z) + mercX(maxLng, z)) / 2;
  const cy = (mercY(minLat, z) + mercY(maxLat, z)) / 2;
  const left = cx - width / 2;
  const top = cy - height / 2;
  const n = 2 ** z;
  const tiles: TileViewport["tiles"] = [];
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx++) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      tiles.push({
        url: `https://tile.openstreetmap.org/${z}/${wx}/${ty}.png`,
        x: tx * TILE - left,
        y: ty * TILE - top,
      });
    }
  }
  return {
    tiles,
    project: (lat, lng) => [mercX(lng, z) - left, mercY(lat, z) - top],
  };
}

export const OSM_TILE_SIZE = TILE;

export function buildRouteMapHtml(lines: PrintRouteLine[], width = 720, height = 380): string {
  const valid = lines
    .map((l) => ({ ...l, points: downsamplePoints(l.points, 1500) }))
    .filter((l) => l.points.length >= 1);
  const vp = computeTileViewport(
    valid.flatMap((l) => l.points),
    width,
    height,
  );
  if (!vp) return "";
  const tiles = vp.tiles.map(
    (t) =>
      `<img src="${t.url}" alt="" style="position:absolute;left:${t.x.toFixed(0)}px;top:${t.y.toFixed(
        0,
      )}px;width:${TILE}px;height:${TILE}px" />`,
  );
  const px = (p: { lat: number; lng: number }) => {
    const [x, y] = vp.project(p.lat, p.lng);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };
  const svg = valid
    .map((l) => {
      const pts = l.points.map(px).join(" ");
      const [sx, sy] = px(l.points[0]).split(",");
      const [ex, ey] = px(l.points[l.points.length - 1]).split(",");
      return `<polyline points="${pts}" fill="none" stroke="#fff" stroke-width="7" stroke-linejoin="round" stroke-linecap="round" opacity="0.9"/>
<polyline points="${pts}" fill="none" stroke="${l.color}" stroke-width="4" stroke-linejoin="round" stroke-linecap="round"/>
<circle cx="${sx}" cy="${sy}" r="6" fill="#16a34a" stroke="#fff" stroke-width="2"/>
<circle cx="${ex}" cy="${ey}" r="6" fill="#dc2626" stroke="#fff" stroke-width="2"/>`;
    })
    .join("");
  return `<div class="route-map" style="position:relative;width:${width}px;max-width:100%;height:${height}px;overflow:hidden;border:1px solid #ddd;border-radius:6px;background:#e5e7eb">
${tiles.join("")}
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="position:absolute;left:0;top:0">${svg}</svg>
<div style="position:absolute;right:0;bottom:0;background:rgba(255,255,255,.8);font-size:9px;padding:1px 4px;color:#333">© OpenStreetMap</div>
</div>`;
}
